<template>
  <div class="guide-subscription-limits">
    <h2>Subscription, seats, and usage limits</h2>
    <p>
      Your subscription plan determines how many packages you can send, how many
      team members can access the platform, which features are available, and how
      authentication credits are allocated. This guide explains what each limit
      means, how usage is tracked, and what happens when a limit is reached.
    </p>

    <q-separator class="q-my-lg" />

    <h2>Seats</h2>
    <p>
      A <strong>seat</strong> is a paid team-member slot on your subscription.
      Every plan includes a base number of seats (typically one). You can purchase
      additional seats at a per-user rate determined by your plan and billing
      interval (monthly or annual).
    </p>

    <h3>What counts against your seat total</h3>
    <p>
      Two things consume seats: <strong>active users</strong> (team members who
      can log in and use the platform) and <strong>pending invitations</strong>
      (invites that have been sent but not yet accepted). A pending invite
      reserves a seat immediately so that accepting the invite is guaranteed to
      succeed.
    </p>
    <p>
      The number of seats you can still assign is:
    </p>
    <p style="font-style: italic; padding-left: 16px;">
      Available seats = Seats purchased &minus; Active users &minus; Pending invites
    </p>
    <p>
      When available seats reaches zero, no new invitations can be sent and no
      deactivated users can be reactivated until a seat is freed or additional
      seats are purchased.
    </p>

    <h3>Purchasing additional seats</h3>
    <p>
      Additional seats can be purchased at any time from
      <strong>Settings &gt; Subscription</strong> in the portal. When you purchase
      seats mid-billing-cycle, the charge is <strong>prorated</strong> based on
      the number of whole days remaining in the current billing period. There is a
      minimum charge of $1.00 per purchase regardless of proration.
    </p>
    <p>
      Purchased seats take effect immediately - the new capacity is available as
      soon as the payment succeeds.
    </p>

    <h3>Removing seats</h3>
    <p>
      Seat removal is <strong>scheduled, not immediate</strong>. When you request
      a reduction, the seats are marked as "pending removal" and continue to be
      billed at the current rate through the end of the billing period. The
      reduction takes effect at the next renewal. There is no mid-cycle refund
      for removed seats.
    </p>
    <p>
      You cannot remove seats below the base number included in your plan.
    </p>

    <h3>What happens when seats decrease at renewal</h3>
    <p>
      If the new seat count (after pending removals take effect) is lower than
      the number of active users and pending invitations, the platform
      automatically frees capacity in this order:
    </p>
    <ol>
      <li>
        <strong>Pending invitations are expired first</strong> (oldest first).
        This is the least disruptive action since no active user loses access.
      </li>
      <li>
        <strong>Active users are suspended</strong> if expiring invitations alone
        is not enough. Users are suspended in order of most recently activated
        first.
      </li>
    </ol>
    <p>
      Suspended users cannot log in until an admin reactivates them (which
      requires an available seat). <strong>Tenant admins are never automatically
      suspended</strong> - this ensures the organisation can always access the
      portal to manage seats and reactivate users.
    </p>

    <q-banner class="bg-amber-1 text-amber-10 q-my-md" rounded>
      <strong>Important:</strong> The platform also automatically drops any
      unused seats at renewal to match actual usage. If you have 10 purchased
      seats but only 4 active users and no pending invites, your seat count
      will be reduced to 4 (or your plan's base seat count, whichever is
      higher) at the next renewal. This prevents paying for seats that are no
      longer in use.
    </q-banner>

    <q-separator class="q-my-lg" />

    <h2>Monthly package limits</h2>
    <p>
      Each plan includes a monthly package allowance - the number of packages you
      can send per billing period. The counter resets to zero at the start of each
      new billing period.
    </p>
    <p>
      What happens when you reach the limit depends on your plan:
    </p>
    <ul>
      <li>
        <strong>Hard cap (no overage pricing):</strong> Sending is blocked until
        the next billing period begins. You will see a warning as you approach
        90% of your limit.
      </li>
      <li>
        <strong>Overage pricing:</strong> Sending continues beyond the included
        allowance. Each additional package incurs an overage fee at the rate
        specified in your plan. Overage charges are billed separately from your
        base subscription.
      </li>
    </ul>

    <h3>Overage billing</h3>
    <p>
      How overage charges are collected depends on your plan's credit model:
    </p>
    <ul>
      <li>
        <strong>Prepay per package:</strong> Each overage package requires an
        immediate payment before the package can be sent. You will be prompted
        to pay at send time.
      </li>
      <li>
        <strong>Credit line:</strong> Overage charges accumulate on your account
        up to a configured threshold. When the threshold is reached, the
        outstanding balance is automatically settled against your payment method.
        If the settlement charge fails, sending is blocked until the balance is
        resolved.
      </li>
    </ul>

    <q-separator class="q-my-lg" />

    <h2>API package limits</h2>
    <p>
      Plans with API access may have a separate monthly limit on packages created
      via the API. This limit is independent of the web-based package limit - the
      two quotas are tracked separately. API packages count against both the API
      limit and the overall monthly package limit.
    </p>
    <p>
      When the API package limit is reached, API calls to create or send packages
      return an error. Packages created through the portal are unaffected.
    </p>

    <q-separator class="q-my-lg" />

    <h2 id="auth-credits">Authentication credits</h2>
    <p>
      SMS, KBA, and ID Verification (IDV) authentication methods each consume
      credits. Your plan includes a free monthly allowance for each type. Beyond
      the free allowance, credits are purchased in packs of 10 at a per-use price
      determined by your plan.
    </p>

    <div class="q-my-md" style="overflow-x: auto;">
      <table class="q-table" style="width: 100%; border-collapse: collapse;">
        <thead>
          <tr>
            <th style="text-align: left; padding: 8px; border-bottom: 2px solid #e0e0e0;">Credit type</th>
            <th style="text-align: left; padding: 8px; border-bottom: 2px solid #e0e0e0;">Consumed when</th>
            <th style="text-align: left; padding: 8px; border-bottom: 2px solid #e0e0e0;">Monthly free allowance</th>
            <th style="text-align: left; padding: 8px; border-bottom: 2px solid #e0e0e0;">Purchased in</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td style="padding: 8px; border-bottom: 1px solid #eee;">SMS</td>
            <td style="padding: 8px; border-bottom: 1px solid #eee;">A signer completes SMS one-time-passcode verification</td>
            <td style="padding: 8px; border-bottom: 1px solid #eee;">Varies by plan</td>
            <td style="padding: 8px; border-bottom: 1px solid #eee;">Packs of 10</td>
          </tr>
          <tr>
            <td style="padding: 8px; border-bottom: 1px solid #eee;">KBA</td>
            <td style="padding: 8px; border-bottom: 1px solid #eee;">A signer completes knowledge-based authentication</td>
            <td style="padding: 8px; border-bottom: 1px solid #eee;">Varies by plan</td>
            <td style="padding: 8px; border-bottom: 1px solid #eee;">Packs of 10</td>
          </tr>
          <tr>
            <td style="padding: 8px; border-bottom: 1px solid #eee;">IDV</td>
            <td style="padding: 8px; border-bottom: 1px solid #eee;">A signer completes government ID + selfie verification</td>
            <td style="padding: 8px; border-bottom: 1px solid #eee;">Varies by plan</td>
            <td style="padding: 8px; border-bottom: 1px solid #eee;">Packs of 10</td>
          </tr>
        </tbody>
      </table>
    </div>

    <p>
      The free monthly allowance resets at the start of each billing period.
      Purchased credits do not expire - they remain on your account until used,
      regardless of billing period.
    </p>
    <p>
      If a signer attempts an authentication method and your account has
      insufficient credits (zero free allowance remaining and no purchased credits),
      the authentication will fail. You can purchase additional credit packs at any
      time from <strong>Settings &gt; Subscription</strong>.
    </p>

    <q-separator class="q-my-lg" />

    <h2>Template limits</h2>
    <p>
      Some plans limit the number of templates you can create. When the limit is
      reached, new template creation is blocked. Deleting an existing template
      frees a slot. Plans with unlimited templates have no restriction.
    </p>

    <q-separator class="q-my-lg" />

    <h2>Feature availability</h2>
    <p>
      Certain features are only available on specific plans. If a feature is not
      included in your plan, the corresponding controls are hidden in the portal
      and the API returns an error if the feature is invoked programmatically.
      Features gated by plan include:
    </p>
    <ul>
      <li>Embedded signing</li>
      <li>Signer payment collection</li>
      <li>SMS, KBA, and ID Verification authentication</li>
      <li>Custom branding</li>
      <li>API access and webhooks</li>
      <li>Signing groups</li>
      <li>Bulk send</li>
      <li>SSO / SCIM provisioning</li>
    </ul>

    <q-separator class="q-my-lg" />

    <h2>Billing period and counter resets</h2>
    <p>
      Your billing period runs from your subscription start date (or last renewal
      date) to the next renewal date - typically one month or one year depending
      on your billing interval. It is <strong>not</strong> tied to the calendar
      month.
    </p>
    <p>
      At each successful renewal, the following counters reset to zero:
    </p>
    <ul>
      <li>Monthly package count (web and API)</li>
      <li>Free authentication credit allowances (SMS, KBA, IDV)</li>
      <li>Outstanding overage balance (settled before renewal)</li>
    </ul>
    <p>
      Purchased credit balances and template counts are <strong>not</strong>
      reset at renewal. Pending seat removals take effect at renewal, and the
      seat count is adjusted accordingly.
    </p>

    <q-separator class="q-my-lg" />

    <h2>Payment failures and grace period</h2>
    <p>
      If a renewal payment fails (for example, an expired payment method or
      insufficient funds), the platform enters a <strong>7-day grace
      period</strong>. During the grace period:
    </p>
    <ul>
      <li>Your subscription remains active and you can continue using the platform.</li>
      <li>The platform retries the payment automatically.</li>
      <li>You will receive notifications prompting you to update your payment method.</li>
    </ul>
    <p>
      If the payment is not resolved within 7 days, your subscription is
      <strong>automatically downgraded to the free tier</strong>. Any outstanding
      overage balance is written off. Your data (packages, templates, documents)
      is preserved, but plan-specific features and limits revert to free-tier
      levels.
    </p>

    <q-banner class="bg-red-1 text-red-9 q-my-md" rounded>
      <strong>Action required:</strong> If you receive a renewal failure
      notification, update your payment method as soon as possible from
      <strong>Settings &gt; Subscription</strong> to avoid an automatic
      downgrade.
    </q-banner>

    <q-separator class="q-my-lg" />

    <h2>Viewing your usage</h2>
    <p>
      Your current usage against all limits is visible in the portal at
      <strong>Settings &gt; Subscription</strong>. The usage dashboard shows:
    </p>
    <ul>
      <li>Packages sent this period vs. your monthly limit</li>
      <li>Active users and pending invites vs. purchased seats</li>
      <li>Authentication credit balances (free allowance consumed + purchased credits remaining)</li>
      <li>Templates created vs. template limit</li>
    </ul>

    <section class="dev-section">
    <h2>For developers and enterprise integration</h2>
    <p>
      Subscription limits are enforced on every API call that creates or sends a
      package. Your integration should handle limit-related errors gracefully.
    </p>

    <h3>Quota check before sending</h3>
    <p>
      The <code>POST /api/v1/packages/{id}/validate</code> endpoint runs the same
      validation as send - including quota checks - without actually sending. Use
      it to surface limit warnings before the user commits.
    </p>

    <h3>Limit-related error responses</h3>
    <div class="q-my-md" style="overflow-x: auto;">
      <table class="q-table" style="width: 100%; border-collapse: collapse;">
        <thead>
          <tr>
            <th style="text-align: left; padding: 8px; border-bottom: 2px solid #e0e0e0;">Status</th>
            <th style="text-align: left; padding: 8px; border-bottom: 2px solid #e0e0e0;">Condition</th>
            <th style="text-align: left; padding: 8px; border-bottom: 2px solid #e0e0e0;">Meaning</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td style="padding: 8px; border-bottom: 1px solid #eee;"><code>429</code></td>
            <td style="padding: 8px; border-bottom: 1px solid #eee;">Monthly package limit exceeded</td>
            <td style="padding: 8px; border-bottom: 1px solid #eee;">Package quota exhausted for this billing period (hard cap)</td>
          </tr>
          <tr>
            <td style="padding: 8px; border-bottom: 1px solid #eee;"><code>429</code></td>
            <td style="padding: 8px; border-bottom: 1px solid #eee;">API package limit exceeded</td>
            <td style="padding: 8px; border-bottom: 1px solid #eee;">API-specific quota exhausted for this billing period</td>
          </tr>
          <tr>
            <td style="padding: 8px; border-bottom: 1px solid #eee;"><code>403</code></td>
            <td style="padding: 8px; border-bottom: 1px solid #eee;">Feature not available</td>
            <td style="padding: 8px; border-bottom: 1px solid #eee;">The requested feature (e.g. embedded signing, webhooks) is not included in the current plan</td>
          </tr>
          <tr>
            <td style="padding: 8px; border-bottom: 1px solid #eee;"><code>402</code></td>
            <td style="padding: 8px; border-bottom: 1px solid #eee;">Overage payment required</td>
            <td style="padding: 8px; border-bottom: 1px solid #eee;">Package exceeds included allowance and overage payment failed or is blocked</td>
          </tr>
        </tbody>
      </table>
    </div>

    <q-separator class="q-my-lg" />

    <h3>MCP tool equivalents</h3>
    <p>
      Subscription limits apply equally to MCP tool calls. When a limit is
      reached, the MCP tool returns the same error. Relevant tools:
    </p>
    <ul>
      <li><code>send_package</code> - blocked by package quota, API quota, or overage limits</li>
      <li><code>create_package</code> - blocked by package quota</li>
      <li><code>validate_package</code> - returns quota warnings without sending</li>
    </ul>

    <q-separator class="q-my-lg" />

    <h3>Best practices</h3>
    <ul>
      <li>
        <strong>Use <code>validate_package</code> before sending.</strong> This
        surfaces quota warnings and feature-availability errors before the send
        attempt, allowing your integration to present a clear message to the user.
      </li>
      <li>
        <strong>Handle 429 and 402 responses.</strong> These indicate quota
        exhaustion or payment issues. Present the user with a message directing
        them to the subscription settings rather than retrying silently.
      </li>
      <li>
        <strong>Monitor credit balances programmatically.</strong> If your
        workflow depends on SMS, KBA, or IDV authentication, check credit
        availability before configuring signers with those methods to avoid
        authentication failures at signing time.
      </li>
    </ul>

    <q-banner class="bg-green-1 text-green-9 q-mt-lg" rounded>
      <template v-slot:avatar>
        <q-icon name="menu_book" color="green-9" />
      </template>
      See the <strong>Subscription</strong> section in the
      <a href="/Docs/ApiReference" class="text-green-9" style="text-decoration: underline;">API Reference</a>
      for complete endpoint schemas.
    </q-banner>
    </section>
  </div>
</template>

<script>
export default {
    name: 'GuideSubscriptionLimits'
};
</script>
